import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { all_routes } from "../../../../router/all_routes";
import TableFilter, { FilterField } from "../../../Common/TableFilter/TableFilter";
import ActionsDropdown, { Action } from "../../../Common/ActionsDropdown";
import GuardianDetailsModal from "../components/GuardianDetails";
import { useGuardians } from "../hooks/useGuardians";
import Table from "../../../../components/Common/dataTable/index";
import EditGuardianModal from "../components/EditGuardianModal";
import AddGuardianModal from "../components/AddGuardianModal";
import { Button, Spin } from "antd";
import { useSearchParams } from "react-router-dom";
import PageHeader from "components/Common/PageHeader";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import CustomButton from "components/Common/ui/CustomButton";
import { useAbility } from "config/abilityContext";
import FamilySituationBadge from "components/Common/Badge/FamilySituationBadge";

const GuardianList: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const [searchParams] = useSearchParams();
  const ability = useAbility();
  const {
    guardians,
    loading,
    error,
    selectedGuardian,
    setSelectedGuardian,
    setEditModalVisible,
    handleUpdate,
    editModalVisible,
    showDetailsModal,
    setShowDetailsModal,
    showAddModal,
    setShowAddModal,
    fetchGuardians,
    triggerResetPassword,
    pagination
  } = useGuardians();

  const [filters, setFilters] = useState<{
    [key: string]: string | number | null;
  }>({});

  const filterFields: FilterField[] = [
    { key: "firstName", label: t("first_name"), type: "text" },
    { key: "lastName", label: t("last_name"), type: "text" },
    { key: "email", label: t("email"), type: "text" },
    { key: "phoneNumber", label: t("phone_number"), type: "text" },
  ];

  const handleFilterApply = (newFilters: { [key: string]: string | number | null }) => {
    setFilters(newFilters);
    fetchGuardians({
      ...newFilters,
      page: 1,
      limit: pagination?.limit || 10,
    });
  };

  const handleFilterReset = () => {
    setFilters({});
    fetchGuardians({
      page: 1,
      limit: pagination?.limit || 10,
    });
  };

  const handleTableChange = (pg: any) => {
    fetchGuardians({
      ...filters,
      page: pg?.current || 1,
      limit: pg?.pageSize || 10,
    });
  };

  const columns = [
    {
      title: t("first_name"),
      dataIndex: ["user", "firstName"],
      sorter: false,
    },
    {
      title: t("last_name"),
      dataIndex: ["user", "lastName"],
      sorter: false,
    },
    {
      title: t("email"),
      dataIndex: ["user", "email"],
      sorter: false,
    },
    {
      title: t("guardianModule.familySituation"),
      dataIndex: ["familySituation"],
      sorter: false,
      render: (familySituation: string) => {
        return <FamilySituationBadge situation={familySituation} size="sm" />;
      },
    },
    {
      title: t("phone_number"),
      align: "center",
      dataIndex: ["user", "phoneNumber"],
      render: (phoneNumber: string) => phoneNumber || "-",
      sorter: false,
    },
    {
      title: t("number_of_children"),
      align: "center",
      render: (text: string, record: any) => {
        return (
          <span>{record?.linkedStudents?.length || 0}</span>
        );
      },
    },
    {
      title: t("action"),
      dataIndex: "action",
      render: (_: any, record: any) => {
        const actions: Action[] = [
          {
            label: t("view_details"),
            icon: "ti ti-eye",
            actionType: "modal",
            onClick: () => {
              setSelectedGuardian(record);
              setShowDetailsModal(true);
            },
          },
          {
            label: t("edit"),
            icon: "ti ti-edit",
            actionType: "modal",
            hidden: !ability.can("update", "Guardian"),
            onClick: () => {
              setSelectedGuardian(record);
              setEditModalVisible(true);
            },
          },
          {
            label: t("reset_password_via_email"),
            icon: "ti ti-mail",
            actionType: "modal",
            onClick: async () => {
              await triggerResetPassword({ user_id: record.user.id, type: "email" })
            },
          },
          // {
          //   label: t("reset_password_via_sms"),
          //   icon: "ti ti-phone",
          //   actionType: "modal",
          //   onClick: async () => {
          //     await triggerResetPassword({ user_id: record.user.id, type: "sms" })
          //   },
          // },
        ];
        return <ActionsDropdown actions={actions} />;
      },
    },
  ];

  useEffect(() => {
    const shouldShowModal = searchParams.get('showAddModal') === 'true';
    setShowAddModal(shouldShowModal);
  }, [searchParams]);

  useEffect(() => {
    fetchGuardians({
      page: 1,
      limit: 10,
    });
  }, []);

  const breadcrumbItems = [
    { label: t("dashboard"), path: "/dashboard" },
    { label: t("guardians"), active: true },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("guardians_list")}
          breadcrumbItems={breadcrumbItems}
        />

        <div className="d-flex mb-3 justify-content-end gap-2">
          {ability.can("create", "Guardian") && (
            <CustomButton
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              <i className="ti ti-plus me-2" />
              {t("add_new_guardian")}
            </CustomButton>
          )}
          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={handleFilterApply}
            onReset={handleFilterReset}
          />
        </div>

        <div className="table-responsive">
          <Table
            loading={loading}
            columns={columns}
            dataSource={guardians}
            pagination={true}
            dataPaginate={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,

            }}
            onChange={handleTableChange}
          />

        </div>

        {/* Details Modal */}
        {selectedGuardian && showDetailsModal && (
          <GuardianDetailsModal
            guardian={selectedGuardian}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedGuardian(null);
            }}
          />
        )}

        {/* Edit Modal */}
        {selectedGuardian && editModalVisible && (
          <EditGuardianModal
            open={editModalVisible}
            guardian={selectedGuardian}
            onClose={() => {
              setEditModalVisible(false);
              setSelectedGuardian(null);
            }}
            onSave={(payload) => handleUpdate(selectedGuardian.id, payload)}
          />
        )}

        {showAddModal && (
          <AddGuardianModal
            visible={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSuccess={() => {
              setShowAddModal(false);
              fetchGuardians({
                ...filters,
                page: pagination?.page || 1,
                limit: pagination?.limit || 10,
              });
            }}
            context="guardian"
          />
        )}
      </div>
    </div>
  );
};

export default GuardianList;